"use client";

import { useEffect, useRef, useState, useCallback } from "react";
import { Box, Text } from "@mantine/core";
import {
  FaceLandmarker,
  FilesetResolver,
} from "@mediapipe/tasks-vision";

// 4 phases, 12 seconds total
const PHASES = [
  { id: "eyes", duration: 3000, text: "Analyzing eye chroma and clarity" },
  { id: "skin", duration: 3000, text: "Mapping undertone depth" },
  { id: "lips", duration: 3000, text: "Measuring lip saturation" },
  { id: "harmony", duration: 3000, text: "Calculating contrast ratio" },
];

interface FaceScanOverlayProps {
  imageSrc: string;
  isAnalyzing: boolean;
  onScanComplete?: () => void;
  currentStepIndex: number;
  /** Override the 4 scan phases (id must key into metrics). Defaults to color-analysis phases. */
  phases?: { id: string; duration: number; text: string }[];
  /** Override the fake HUD readouts per phase id. Defaults to color-analysis metrics. */
  metrics?: Record<string, { label: string; value: string | number; unit: string }[]>;
}

// Feature landmark indices
const CONTOURS = {
  leftEye: [33, 7, 163, 144, 145, 153, 154, 155, 133, 173, 157, 158, 159, 160, 161, 246, 33],
  rightEye: [362, 382, 381, 380, 374, 373, 390, 249, 263, 466, 388, 387, 386, 385, 384, 398, 362],
  lips: [61, 146, 91, 181, 84, 17, 314, 405, 321, 375, 291, 409, 270, 269, 267, 0, 37, 39, 40, 185, 61],
  leftCheek: [116, 117, 118, 119, 100, 126, 209, 49, 129, 203, 206, 216],
  rightCheek: [345, 346, 347, 348, 329, 355, 429, 279, 358, 423, 426, 436],
  jawline: [172, 136, 150, 149, 176, 148, 152, 377, 400, 378, 379, 365, 397],
  noseBridge: [6, 197, 195, 5, 4],
  foreheadLine: [54, 103, 67, 109, 10, 338, 297, 332, 284],
};

// Fake but realistic metrics for each phase
const PHASE_METRICS = {
  eyes: [
    { label: "CHROMA", value: 72, unit: "%" },
    { label: "CLARITY", value: 0.84, unit: "" },
    { label: "DEPTH", value: "MED", unit: "" },
  ],
  skin: [
    { label: "WARMTH", value: "+12", unit: "°" },
    { label: "UNDERTONE", value: "WARM", unit: "" },
    { label: "DEPTH", value: 0.67, unit: "" },
  ],
  lips: [
    { label: "SAT", value: 68, unit: "%" },
    { label: "VALUE", value: 0.72, unit: "" },
    { label: "CONTRAST", value: "HIGH", unit: "" },
  ],
  harmony: [
    { label: "RATIO", value: "1:2.4", unit: "" },
    { label: "BALANCE", value: 91, unit: "%" },
    { label: "SEASON", value: "CALC...", unit: "" },
  ],
};

export function FaceScanOverlay({
  imageSrc,
  isAnalyzing,
  phases: PHASES_PROP,
  metrics: METRICS_PROP,
}: FaceScanOverlayProps) {
  const PHASES_ACTIVE = PHASES_PROP || PHASES;
  const METRICS_ACTIVE = METRICS_PROP || PHASE_METRICS;
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);
  const faceLandmarkerRef = useRef<FaceLandmarker | null>(null);
  const animationFrameRef = useRef<number | null>(null);
  const phaseStartTimeRef = useRef<number>(Date.now());
  const landmarksRef = useRef<{ x: number; y: number; z: number }[] | null>(null);
  const globalStartTimeRef = useRef<number>(Date.now());

  const [currentPhase, setCurrentPhase] = useState(0);
  const [landmarks, setLandmarks] = useState<{ x: number; y: number; z: number }[] | null>(null);
  const [isModelLoaded, setIsModelLoaded] = useState(false);
  const [imageFilter, setImageFilter] = useState("none");

  // Easing functions
  const easeOut = (t: number) => 1 - Math.pow(1 - t, 3);
  const easeInOut = (t: number) => t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;

  // Sync landmarks ref
  useEffect(() => {
    landmarksRef.current = landmarks;
  }, [landmarks]);

  // Initialize MediaPipe
  useEffect(() => {
    let mounted = true;

    async function init() {
      try {
        const filesetResolver = await FilesetResolver.forVisionTasks(
          "https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.8/wasm"
        );

        const landmarker = await FaceLandmarker.createFromOptions(filesetResolver, {
          baseOptions: {
            modelAssetPath:
              "https://storage.googleapis.com/mediapipe-models/face_landmarker/face_landmarker/float16/1/face_landmarker.task",
            delegate: "GPU",
          },
          runningMode: "IMAGE",
          numFaces: 1,
          outputFaceBlendshapes: false,
          outputFacialTransformationMatrixes: false,
        });

        if (mounted) {
          faceLandmarkerRef.current = landmarker;
          setIsModelLoaded(true);
        }
      } catch (error) {
        console.error("Failed to load face landmarker:", error);
        if (mounted) setIsModelLoaded(true);
      }
    }

    init();
    return () => {
      mounted = false;
      faceLandmarkerRef.current?.close();
    };
  }, []);

  // Detect face when image loads
  useEffect(() => {
    if (!isModelLoaded || !imageSrc || !imageRef.current) return;

    const img = imageRef.current;
    const detect = () => {
      if (!faceLandmarkerRef.current || !img.complete) return;
      try {
        const result = faceLandmarkerRef.current.detect(img);
        if (result.faceLandmarks?.[0]) {
          setLandmarks(result.faceLandmarks[0]);
        }
      } catch (e) {
        console.error("Face detection error:", e);
      }
    };

    if (img.complete) detect();
    else img.onload = detect;
  }, [isModelLoaded, imageSrc]);

  // Phase progression
  useEffect(() => {
    if (!isAnalyzing) return;

    globalStartTimeRef.current = Date.now();
    let phaseTimeout: NodeJS.Timeout;

    const advancePhase = (phaseIndex: number) => {
      if (phaseIndex >= PHASES_ACTIVE.length) return;

      setCurrentPhase(phaseIndex);
      phaseStartTimeRef.current = Date.now();

      if (phaseIndex < PHASES_ACTIVE.length - 1) {
        phaseTimeout = setTimeout(() => {
          advancePhase(phaseIndex + 1);
        }, PHASES_ACTIVE[phaseIndex].duration);
      }
    };

    advancePhase(0);

    return () => {
      if (phaseTimeout) clearTimeout(phaseTimeout);
    };
  }, [isAnalyzing]);

  // Main animation loop
  const animate = useCallback(() => {
    const canvas = canvasRef.current;
    const image = imageRef.current;

    if (!canvas || !image || !image.complete || image.naturalWidth === 0) {
      animationFrameRef.current = requestAnimationFrame(animate);
      return;
    }

    const ctx = canvas.getContext("2d");
    if (!ctx) {
      animationFrameRef.current = requestAnimationFrame(animate);
      return;
    }

    // Sync canvas size
    const rect = image.getBoundingClientRect();
    if (canvas.width !== rect.width || canvas.height !== rect.height) {
      canvas.width = rect.width;
      canvas.height = rect.height;
    }

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    const now = Date.now();
    const globalTime = now - globalStartTimeRef.current;
    const phaseElapsed = now - phaseStartTimeRef.current;
    const phaseDuration = PHASES_ACTIVE[currentPhase]?.duration || 3000;
    const phaseProgress = Math.min(phaseElapsed / phaseDuration, 1);

    // Fade envelope
    let envelope = 1;
    if (phaseProgress < 0.15) {
      envelope = easeOut(phaseProgress / 0.15);
    } else if (phaseProgress > 0.85) {
      envelope = easeOut((1 - phaseProgress) / 0.15);
    }

    const phaseId = PHASES_ACTIVE[currentPhase]?.id || PHASES_ACTIVE[0]?.id || "eyes";
    const lm = landmarksRef.current;
    const hasLandmarks = lm && lm.length > 400;

    if (!hasLandmarks) {
      animationFrameRef.current = requestAnimationFrame(animate);
      return;
    }

    // The <img> uses object-fit: cover, so the displayed picture is a
    // center-cropped, uniformly-scaled version of the natural image — NOT a
    // simple stretch. MediaPipe landmarks are fractions of the NATURAL image,
    // so we must replay the same cover transform (scale up by the larger axis
    // ratio, then center-offset) or every box drifts. The eyes drift most
    // because they sit high, where vertical cropping bites hardest.
    const nW = image.naturalWidth;
    const nH = image.naturalHeight;
    // Uniform scale that makes the natural image cover the canvas box.
    const coverScale = Math.max(canvas.width / nW, canvas.height / nH);
    // Centered offsets: half of the overflow that gets cropped on each axis.
    const offsetX = (canvas.width - nW * coverScale) / 2;
    const offsetY = (canvas.height - nH * coverScale) / 2;

    const toCanvas = (idx: number) => {
      const safeIdx = Math.min(idx, lm.length - 1);
      // Landmark fraction → natural pixel → cover-scaled → centered on canvas.
      return {
        x: lm[safeIdx].x * nW * coverScale + offsetX,
        y: lm[safeIdx].y * nH * coverScale + offsetY,
      };
    };

    const getCenter = (indices: number[]) => {
      let cx = 0, cy = 0;
      indices.forEach(i => {
        const p = toCanvas(i);
        cx += p.x;
        cy += p.y;
      });
      return { x: cx / indices.length, y: cy / indices.length };
    };

    const getBounds = (indices: number[]) => {
      const points = indices.map(i => toCanvas(i));
      const xs = points.map(p => p.x);
      const ys = points.map(p => p.y);
      return {
        minX: Math.min(...xs),
        maxX: Math.max(...xs),
        minY: Math.min(...ys),
        maxY: Math.max(...ys),
      };
    };

    // ========================================
    // HUD CORNER BRACKETS
    // ========================================
    const drawHUDCorners = () => {
      const margin = 12;
      const cornerSize = 20;
      const w = canvas.width;
      const h = canvas.height;

      ctx.save();
      ctx.strokeStyle = `rgba(255, 255, 255, ${0.6 * envelope})`;
      ctx.lineWidth = 2;
      ctx.lineCap = "square";

      // Top-left
      ctx.beginPath();
      ctx.moveTo(margin, margin + cornerSize);
      ctx.lineTo(margin, margin);
      ctx.lineTo(margin + cornerSize, margin);
      ctx.stroke();

      // Top-right
      ctx.beginPath();
      ctx.moveTo(w - margin - cornerSize, margin);
      ctx.lineTo(w - margin, margin);
      ctx.lineTo(w - margin, margin + cornerSize);
      ctx.stroke();

      // Bottom-left
      ctx.beginPath();
      ctx.moveTo(margin, h - margin - cornerSize);
      ctx.lineTo(margin, h - margin);
      ctx.lineTo(margin + cornerSize, h - margin);
      ctx.stroke();

      // Bottom-right
      ctx.beginPath();
      ctx.moveTo(w - margin - cornerSize, h - margin);
      ctx.lineTo(w - margin, h - margin);
      ctx.lineTo(w - margin, h - margin - cornerSize);
      ctx.stroke();

      ctx.restore();
    };

    // ========================================
    // SCANNING GRID
    // ========================================
    const drawScanGrid = () => {
      const gridSpacing = 40;
      const scanLineY = ((globalTime * 0.08) % (canvas.height + 100)) - 50;

      ctx.save();

      // Vertical lines (faint)
      ctx.strokeStyle = `rgba(255, 255, 255, ${0.06 * envelope})`;
      ctx.lineWidth = 1;
      for (let x = gridSpacing; x < canvas.width; x += gridSpacing) {
        ctx.beginPath();
        ctx.moveTo(x, 0);
        ctx.lineTo(x, canvas.height);
        ctx.stroke();
      }

      // Horizontal lines (faint)
      for (let y = gridSpacing; y < canvas.height; y += gridSpacing) {
        ctx.beginPath();
        ctx.moveTo(0, y);
        ctx.lineTo(canvas.width, y);
        ctx.stroke();
      }

      // Scanning line (bright, moves down)
      const gradient = ctx.createLinearGradient(0, scanLineY - 30, 0, scanLineY + 30);
      gradient.addColorStop(0, "rgba(255, 255, 255, 0)");
      gradient.addColorStop(0.5, `rgba(255, 255, 255, ${0.4 * envelope})`);
      gradient.addColorStop(1, "rgba(255, 255, 255, 0)");

      ctx.strokeStyle = gradient;
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(0, scanLineY);
      ctx.lineTo(canvas.width, scanLineY);
      ctx.stroke();

      ctx.restore();
    };

    // ========================================
    // MEASUREMENT BRACKETS around features
    // ========================================
    const drawMeasurementBrackets = (indices: number[], padding: number = 8) => {
      const bounds = getBounds(indices);
      const bracketSize = 8;

      ctx.save();
      ctx.strokeStyle = `rgba(255, 255, 255, ${0.7 * envelope})`;
      ctx.lineWidth = 1.5;
      ctx.lineCap = "square";

      const x1 = bounds.minX - padding;
      const x2 = bounds.maxX + padding;
      const y1 = bounds.minY - padding;
      const y2 = bounds.maxY + padding;

      // Top-left bracket
      ctx.beginPath();
      ctx.moveTo(x1, y1 + bracketSize);
      ctx.lineTo(x1, y1);
      ctx.lineTo(x1 + bracketSize, y1);
      ctx.stroke();

      // Top-right bracket
      ctx.beginPath();
      ctx.moveTo(x2 - bracketSize, y1);
      ctx.lineTo(x2, y1);
      ctx.lineTo(x2, y1 + bracketSize);
      ctx.stroke();

      // Bottom-left bracket
      ctx.beginPath();
      ctx.moveTo(x1, y2 - bracketSize);
      ctx.lineTo(x1, y2);
      ctx.lineTo(x1 + bracketSize, y2);
      ctx.stroke();

      // Bottom-right bracket
      ctx.beginPath();
      ctx.moveTo(x2 - bracketSize, y2);
      ctx.lineTo(x2, y2);
      ctx.lineTo(x2, y2 - bracketSize);
      ctx.stroke();

      ctx.restore();

      return { x1, x2, y1, y2 };
    };

    // ========================================
    // DATA READOUT near features
    // ========================================
    const drawDataReadout = (x: number, y: number, metrics: { label: string; value: string | number; unit: string }[], align: "left" | "right" = "right") => {
      ctx.save();
      ctx.font = "bold 9px monospace";

      const lineHeight = 14;
      const startY = y;

      metrics.forEach((metric, i) => {
        // Animate value appearing
        const delay = i * 0.15;
        const metricProgress = Math.max(0, Math.min(1, (phaseProgress - delay) / 0.3));
        const alpha = easeOut(metricProgress) * envelope;

        if (alpha > 0) {
          const yPos = startY + i * lineHeight;

          // Label
          ctx.fillStyle = `rgba(180, 180, 180, ${alpha * 0.8})`;
          ctx.textAlign = align;
          ctx.fillText(metric.label, x, yPos);

          // Value (offset)
          const valueOffset = align === "right" ? -45 : 45;
          ctx.fillStyle = `rgba(255, 255, 255, ${alpha})`;
          ctx.textAlign = align === "right" ? "left" : "right";
          const displayValue = typeof metric.value === "number"
            ? metric.value.toFixed(metric.unit === "%" ? 0 : 2)
            : metric.value;
          ctx.fillText(`${displayValue}${metric.unit}`, x + valueOffset, yPos);
        }
      });

      ctx.restore();
    };

    // ========================================
    // CONNECTION LINES between features
    // ========================================
    const drawConnectionLine = (from: { x: number; y: number }, to: { x: number; y: number }, progress: number) => {
      const lineProgress = easeInOut(Math.min(1, progress * 2));
      if (lineProgress <= 0) return;

      const midX = from.x + (to.x - from.x) * lineProgress;
      const midY = from.y + (to.y - from.y) * lineProgress;

      ctx.save();
      ctx.strokeStyle = `rgba(255, 255, 255, ${0.3 * envelope})`;
      ctx.lineWidth = 1;
      ctx.setLineDash([4, 4]);

      ctx.beginPath();
      ctx.moveTo(from.x, from.y);
      ctx.lineTo(midX, midY);
      ctx.stroke();

      // Dot at end
      ctx.fillStyle = `rgba(255, 255, 255, ${0.8 * envelope * lineProgress})`;
      ctx.beginPath();
      ctx.arc(midX, midY, 3, 0, Math.PI * 2);
      ctx.fill();

      ctx.restore();
    };

    // ========================================
    // CONTOUR WITH MULTIPLE SCAN LINES
    // ========================================
    const drawContourWithMultiScan = (indices: number[], lineWidth: number, numScans: number = 3) => {
      const points = indices.map(i => toCanvas(i));
      const totalPoints = points.length;

      let totalLength = 0;
      const segmentLengths: number[] = [0];
      for (let i = 1; i < points.length; i++) {
        const dx = points[i].x - points[i - 1].x;
        const dy = points[i].y - points[i - 1].y;
        totalLength += Math.sqrt(dx * dx + dy * dy);
        segmentLengths.push(totalLength);
      }

      // Draw base contour
      ctx.save();
      ctx.strokeStyle = `rgba(255, 255, 255, ${0.2 * envelope})`;
      ctx.lineWidth = lineWidth;
      ctx.lineCap = "round";
      ctx.lineJoin = "round";

      ctx.beginPath();
      ctx.moveTo(points[0].x, points[0].y);
      for (let i = 1; i < totalPoints; i++) {
        ctx.lineTo(points[i].x, points[i].y);
      }
      ctx.stroke();
      ctx.restore();

      // Draw multiple traveling highlights
      for (let s = 0; s < numScans; s++) {
        const offset = s / numScans;
        const speed = 0.8 + s * 0.2;
        const scanPosition = ((globalTime * speed) / 1500 + offset) % 1;
        const scanLength = 0.15;

        ctx.save();
        ctx.strokeStyle = `rgba(255, 255, 255, ${(0.7 - s * 0.15) * envelope})`;
        ctx.lineWidth = lineWidth + 0.5 - s * 0.2;
        ctx.lineCap = "round";
        ctx.shadowColor = "rgba(255, 255, 255, 0.5)";
        ctx.shadowBlur = 6;

        const highlightStart = scanPosition;
        const highlightEnd = (scanPosition + scanLength) % 1;

        ctx.beginPath();
        let inHighlight = false;

        for (let i = 0; i < totalPoints; i++) {
          const progress = totalLength > 0 ? segmentLengths[i] / totalLength : i / totalPoints;

          let isInHighlight: boolean;
          if (highlightEnd > highlightStart) {
            isInHighlight = progress >= highlightStart && progress <= highlightEnd;
          } else {
            isInHighlight = progress >= highlightStart || progress <= highlightEnd;
          }

          if (isInHighlight) {
            if (!inHighlight) {
              ctx.moveTo(points[i].x, points[i].y);
              inHighlight = true;
            } else {
              ctx.lineTo(points[i].x, points[i].y);
            }
          } else {
            inHighlight = false;
          }
        }

        ctx.stroke();
        ctx.restore();
      }
    };

    // ========================================
    // ALWAYS DRAW: HUD + Grid
    // ========================================
    drawHUDCorners();
    drawScanGrid();

    // ========================================
    // CAMERA LENS EFFECT - Shifting hue & focus
    // DELETE THIS SECTION TO REVERT
    // ========================================
    const applyLensEffect = () => {
      // Oscillating hue shift - like white balance hunting
      const hueShift = Math.sin(globalTime / 800) * 6 + Math.sin(globalTime / 1200) * 4;

      // Focus breathing - subtle blur oscillation
      const focusBreath = Math.sin(globalTime / 1500) * 0.3 + 0.3; // 0 to 0.6px blur

      // Slight exposure flicker
      const exposureFlicker = 1 + Math.sin(globalTime / 600) * 0.015;

      // Chromatic shift feeling
      const satShift = 1 + Math.sin(globalTime / 1000) * 0.04;

      return `hue-rotate(${hueShift}deg) blur(${focusBreath}px) brightness(${exposureFlicker}) saturate(${satShift})`;
    };

    // Apply base lens effect, then phase effects layer on top
    const baseLensEffect = applyLensEffect();
    // ========================================
    // END CAMERA LENS EFFECT
    // ========================================

    // ========================================
    // PHASE: EYES
    // ========================================
    if (phaseId === "eyes") {
      drawContourWithMultiScan(CONTOURS.leftEye, 1.2, 3);
      drawContourWithMultiScan(CONTOURS.rightEye, 1.2, 3);

      const leftBounds = drawMeasurementBrackets(CONTOURS.leftEye);
      const rightBounds = drawMeasurementBrackets(CONTOURS.rightEye);

      // Data readouts
      drawDataReadout(leftBounds.x1 - 8, leftBounds.y1, METRICS_ACTIVE.eyes || PHASE_METRICS.eyes, "right");

      // Connection line between eyes
      const leftCenter = getCenter(CONTOURS.leftEye);
      const rightCenter = getCenter(CONTOURS.rightEye);
      drawConnectionLine(leftCenter, rightCenter, phaseProgress);

      // Camera focus effect + lens effect
      const focusProgress = easeOut(phaseProgress);
      const contrast = 1 + focusProgress * envelope * 0.08;
      // Combine with base lens effect
      setImageFilter(`${baseLensEffect} contrast(${contrast})`);
    }

    // ========================================
    // PHASE: SKIN
    // ========================================
    if (phaseId === "skin") {
      drawContourWithMultiScan(CONTOURS.leftCheek, 1.3, 2);
      drawContourWithMultiScan(CONTOURS.rightCheek, 1.3, 2);
      drawContourWithMultiScan(CONTOURS.noseBridge, 1.0, 2);

      const leftBounds = drawMeasurementBrackets(CONTOURS.leftCheek, 12);
      drawMeasurementBrackets(CONTOURS.rightCheek, 12);

      // Data readout on left side
      drawDataReadout(leftBounds.x1 - 8, leftBounds.y1, METRICS_ACTIVE.skin || PHASE_METRICS.skin, "right");

      // Connection lines showing undertone analysis
      const leftCheekCenter = getCenter(CONTOURS.leftCheek);
      const rightCheekCenter = getCenter(CONTOURS.rightCheek);
      const noseCenter = getCenter(CONTOURS.noseBridge);
      drawConnectionLine(leftCheekCenter, noseCenter, phaseProgress * 0.8);
      drawConnectionLine(rightCheekCenter, noseCenter, phaseProgress * 0.8);

      // White balance effect + lens effect
      const calibration = Math.sin(phaseProgress * Math.PI);
      const tempShift = calibration * envelope * 8;
      setImageFilter(`${baseLensEffect} hue-rotate(${tempShift}deg)`);
    }

    // ========================================
    // PHASE: LIPS
    // ========================================
    if (phaseId === "lips") {
      drawContourWithMultiScan(CONTOURS.lips, 1.3, 3);

      const lipBounds = drawMeasurementBrackets(CONTOURS.lips, 6);

      // Data readout below lips
      drawDataReadout(lipBounds.x2 + 12, lipBounds.y1, METRICS_ACTIVE.lips || PHASE_METRICS.lips, "left");

      // Connection to chin for contrast measurement
      const lipCenter = getCenter(CONTOURS.lips);
      const jawCenter = getCenter(CONTOURS.jawline);
      drawConnectionLine(lipCenter, jawCenter, phaseProgress);

      // Exposure effect + lens effect
      const meterProgress = easeInOut(phaseProgress);
      const exposure = 1 + (1 - meterProgress) * envelope * 0.04;
      setImageFilter(`${baseLensEffect} brightness(${exposure})`);
    }

    // ========================================
    // PHASE: HARMONY
    // ========================================
    if (phaseId === "harmony") {
      drawContourWithMultiScan(CONTOURS.jawline, 1.4, 2);
      drawContourWithMultiScan(CONTOURS.foreheadLine, 1.2, 2);

      const jawBounds = drawMeasurementBrackets(CONTOURS.jawline, 10);

      // Draw forehead arc
      const forehead = toCanvas(10);
      const leftTemple = toCanvas(54);
      const rightTemple = toCanvas(284);
      const arcWidth = Math.abs(rightTemple.x - leftTemple.x);
      const hairRadius = arcWidth * 0.55;
      const arcCenterY = forehead.y + hairRadius * 0.15;

      ctx.save();
      ctx.strokeStyle = `rgba(255, 255, 255, ${0.25 * envelope})`;
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.arc(forehead.x, arcCenterY, hairRadius, Math.PI * 1.15, Math.PI * 1.85);
      ctx.stroke();
      ctx.restore();

      // Data readout
      drawDataReadout(jawBounds.x2 + 12, jawBounds.y1 + 20, METRICS_ACTIVE.harmony || PHASE_METRICS.harmony, "left");

      // Full face connection lines
      const eyeCenter = getCenter([...CONTOURS.leftEye, ...CONTOURS.rightEye]);
      const lipCenter = getCenter(CONTOURS.lips);
      const foreheadCenter = { x: forehead.x, y: forehead.y - 20 };

      drawConnectionLine(foreheadCenter, eyeCenter, phaseProgress * 0.6);
      drawConnectionLine(eyeCenter, lipCenter, phaseProgress * 0.8);
      drawConnectionLine(lipCenter, getCenter(CONTOURS.jawline), phaseProgress);

      // Final lock-in effect + lens effect (settling down)
      const lockIn = easeOut(phaseProgress);
      // Lens effect fades out as we "lock in" the final reading
      const lensIntensity = 1 - lockIn * 0.7;
      const fadedLens = `hue-rotate(${Math.sin(globalTime / 800) * 6 * lensIntensity}deg) blur(${(Math.sin(globalTime / 1500) * 0.3 + 0.3) * lensIntensity}px)`;
      setImageFilter(`${fadedLens} contrast(${1 + lockIn * envelope * 0.06}) saturate(${1 + lockIn * envelope * 0.08})`);
    }

    animationFrameRef.current = requestAnimationFrame(animate);
  }, [currentPhase]);

  // Start animation
  useEffect(() => {
    phaseStartTimeRef.current = Date.now();
    globalStartTimeRef.current = Date.now();
    animationFrameRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [animate]);

  // Reset when not analyzing
  useEffect(() => {
    if (!isAnalyzing) {
      setCurrentPhase(0);
      setImageFilter("none");
    }
  }, [isAnalyzing]);

  const currentText = PHASES_ACTIVE[currentPhase]?.text || "Analyzing...";

  return (
    <Box style={{ position: "relative", width: "100%" }}>
      <img
        ref={imageRef}
        src={imageSrc}
        alt="Your photo"
        crossOrigin="anonymous"
        style={{
          display: "block",
          width: "100%",
          height: "auto",
          maxHeight: 480,
          objectFit: "cover",
          borderRadius: 24,
          filter: imageFilter,
          transition: "filter 0.15s ease-out",
        }}
      />

      <canvas
        ref={canvasRef}
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          pointerEvents: "none",
          borderRadius: 24,
        }}
      />

      {isAnalyzing && (
        <Box
          style={{
            position: "absolute",
            bottom: 16,
            left: "50%",
            transform: "translateX(-50%)",
            background: "rgba(0, 0, 0, 0.85)",
            backdropFilter: "blur(16px)",
            padding: "10px 20px",
            borderRadius: 8,
            whiteSpace: "nowrap",
            border: "1px solid rgba(255,255,255,0.2)",
          }}
        >
          <Text
            fz="xs"
            fw={600}
            c="white"
            style={{
              fontFamily: "monospace",
              letterSpacing: "1px",
              textTransform: "uppercase",
            }}
          >
            {currentText}
          </Text>
        </Box>
      )}
    </Box>
  );
}
